import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import Page from "components/page/Page";
import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import Input from "components/form/input/Input";
import Toggle from "components/form/toggle/Toggle";
import "./editRatePlan.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarsFilter, faEllipsisVertical } from "@fortawesome/pro-regular-svg-icons";
import { StatusCode } from "api/protocols";
import FormLayout from "components/form/FormLayout";
import Sheet from "components/sheet/Sheet";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import { IAccommodationUnitType } from "../unitTypes/UnitTypes";
import { IAccommodationRatePlan } from "./RatePlans";
import {
  GetAccommodationRatePlans,
  PutAccommodationRatePlanTaxLines,
} from "api/rpc/2024-04/facilityAdmin/accommodation/ratePlans/ratePlan";
import {
  GetAccommodationRatePlanPrices,
  IAccommodationRatePlanPrice,
  PutAccommodationRatePlanPrice,
} from "api/rpc/2024-04/facilityAdmin/accommodation/ratePlans/ratePlanPrices";
import { GetAccommodationUnitTypes } from "api/rpc/2024-04/facilityAdmin/accommodation/unitTypes";
import Tabs from "components/tabs/Tabs";
import { GetTaxLine } from "api/rpc/2024-04/facilityAdmin/client/taxLines";
import { TTaxLines } from "redux/reducers/models/facility";
import { ignoreOrderCompare } from "helpers/Helpers";
import SelectNew from "components/select/SelectNew";

interface IUnitTypeWithPrices extends IAccommodationUnitType {
  min_price?: number;
  max_price?: number;
}

const tabs = [
  {
    id: "unitTypes",
    content: "Unit Types",
  },
  {
    id: "taxLines",
    content: "Tax lines",
  },
];

export function EditRatePlan() {
  const { t } = useTranslation();
  const history = useHistory();
  const { ratePlanId }: any = useParams();
  const dispatch = useAppDispatch();

  const [ratePlan, setRatePlan] = useState<IAccommodationRatePlan | null>(null);
  const [ratePlanPrices, setRatePlanPrices] = useState<IAccommodationRatePlanPrice[]>([]);
  const [unitTypes, setUnitTypes] = useState<IUnitTypeWithPrices[]>([]);
  const [tableState, setTableState] = useState({
    limit: 10,
    offset: 0,
  });

  const [selectedTaxLine, setSelectedTaxLine] = useState<number[]>([]);
  const [selectedTaxLineBeforeChanges, setSelectedTaxLineBeforeChanges] = useState<number[]>([]);

  const [detailsCard, setDetailsCard] = useState({
    taxLines: [] as TTaxLines[],
    tax_line_id: null,
    internal_description_visible: false,
  });

  const [accommodationRatePlanUpdateState, setAccommodationRatePlanUpdateState] = useState({
    setLengthOfStayOpen: false,
    updatedMinLos: "",
    updatedMaxLos: "",
    unitType: null,
    setAdjustPriceOpen: false,
    updatedPrice: "",
  });

  const [selected, setSelected] = useState<number>(0);

  const handleTabChange = (index: number) => {
    setSelected(index);
  };

  useEffect(() => {
    void loadAccommodationRatePlan();
    void loadAccommodationRatePlanInformation();
    void loadTaxLines();
  }, []);

  const loadAccommodationRatePlan = async () => {
    const params = {
      id: ratePlanId,
    };

    const res = await GetAccommodationRatePlans(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading rate plan")));
      return;
    }

    const fetchedRatePlan = res.data[0];
    setRatePlan(fetchedRatePlan as IAccommodationRatePlan);

    // Initialize tax lines from the rate plan if they exist
    const taxLineIds = fetchedRatePlan.tax_lines
      ? fetchedRatePlan.tax_lines.map((t: any) => Number(t.id))
      : fetchedRatePlan.tax_line_ids ?? [];
    setSelectedTaxLine(taxLineIds as number[]);
    setSelectedTaxLineBeforeChanges(taxLineIds as number[]);
  };

  const loadAccommodationRatePlanInformation = async () => {
    const priceParams = {
      rate_plan_id: Number(ratePlanId),
    };
    const priceRes = await GetAccommodationRatePlanPrices(priceParams, true);

    if (priceRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading rate plan prices")));
      return;
    }

    const unitTypeRes = await GetAccommodationUnitTypes(null, false);

    if (unitTypeRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading unit types")));
      return;
    }

    const fetchedUnitTypes = (unitTypeRes.data?.unit_types ??
      (Array.isArray(unitTypeRes.data) ? unitTypeRes.data : [])) as IAccommodationUnitType[];
    const taxLines = unitTypeRes.data?.tax_lines;

    if (taxLines) {
      const taxLineIds: number[] = taxLines.map((tax: any) => Number(tax.id));
      setSelectedTaxLine(taxLineIds);
      setSelectedTaxLineBeforeChanges(taxLineIds);
    }

    let prices: IAccommodationRatePlanPrice[] = [];

    if (priceRes.status === StatusCode.OK) {
      prices = priceRes.data;
    }

    const processedUnitTypes: IUnitTypeWithPrices[] = fetchedUnitTypes.map(unit => {
      const unitPrices = prices.filter(p => p.unit_type_id === unit.id);

      if (unitPrices.length === 0) {
        return { ...unit };
      }

      const priceValues = unitPrices.map(p => p.price);
      const minPrice = Math.min(...priceValues);
      const maxPrice = Math.max(...priceValues);

      return {
        ...unit,
        min_price: minPrice,
        max_price: maxPrice,
      };
    });

    setUnitTypes(processedUnitTypes);
    setRatePlanPrices(prices);
  };

  const loadTaxLines = async () => {
    const res = await GetTaxLine(null, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tax lines")));
      return;
    }

    setDetailsCard(prev => ({ ...prev, taxLines: res.data }));
  };

  function getTaxNames() {
    const taxNames = [];
    for (let i = 0; i < selectedTaxLine.length; i++) {
      taxNames[i] = detailsCard.taxLines.find(tax => tax.id === selectedTaxLine[i])?.name ?? "";
    }
    return taxNames.join(", ");
  }

  const toggleTaxLines = (value: number) => {
    setSelectedTaxLine(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  };

  async function updateLos() {
    let params;

    if (
      accommodationRatePlanUpdateState?.unitType?.id === null ||
      accommodationRatePlanUpdateState?.unitType?.id === undefined
    ) {
      const allIds: number[] = ratePlanPrices.map((p: { id: any }) => Number(p.id));

      params = {
        rate_plan_price_id: ratePlanId,
        rate_plan_price_ids: allIds,
        min_los: Number(accommodationRatePlanUpdateState?.updatedMinLos),
        max_los:
          Number(accommodationRatePlanUpdateState?.updatedMaxLos) > 0
            ? Number(accommodationRatePlanUpdateState?.updatedMaxLos)
            : null,
      };
    } else {
      const allIds: number[] = ratePlanPrices
        ?.filter((p: { unit_type_id: any }) => p.unit_type_id === accommodationRatePlanUpdateState?.unitType?.id)
        .map((p: { id: any }) => Number(p.id));

      params = {
        rate_plan_price_id: ratePlanId,
        rate_plan_price_ids: allIds,
        min_los: Number(accommodationRatePlanUpdateState?.updatedMinLos),
        max_los:
          Number(accommodationRatePlanUpdateState?.updatedMaxLos) > 0
            ? Number(accommodationRatePlanUpdateState?.updatedMaxLos)
            : null,
      };
    }

    const putRes = await PutAccommodationRatePlanPrice(params, true);

    if (putRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating length of stay")));
      return;
    }

    dispatch(showSuccess(t("Successfully updated length of stay")));

    setAccommodationRatePlanUpdateState(prevState => ({
      ...prevState,
      setLengthOfStayOpen: false,
      unitType: null,
      updatedMinLos: "",
      updatedMaxLos: "",
    }));

    void loadAccommodationRatePlanInformation();
  }

  async function adjustRatePlanPrice() {
    let params;

    if (
      accommodationRatePlanUpdateState?.unitType?.id === null ||
      accommodationRatePlanUpdateState?.unitType?.id === undefined
    ) {
      const allIds: number[] = ratePlanPrices.map((p: { id: any }) => Number(p.id));

      params = {
        price: Number(accommodationRatePlanUpdateState?.updatedPrice),
        rate_plan_price_id: ratePlanId,
        rate_plan_price_ids: allIds,
      };
    } else {
      const allIds: number[] = ratePlanPrices
        ?.filter((p: { unit_type_id: any }) => p.unit_type_id === accommodationRatePlanUpdateState?.unitType?.id)
        .map((p: { id: any }) => Number(p.id));

      params = {
        price: Number(accommodationRatePlanUpdateState?.updatedPrice),
        rate_plan_price_id: ratePlanId,
        rate_plan_price_ids: allIds,
      };
    }

    const putRes = await PutAccommodationRatePlanPrice(params, true);

    if (putRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating prices")));
      return;
    }

    dispatch(showSuccess(t("Successfully updated prices")));

    setAccommodationRatePlanUpdateState(prevState => ({
      ...prevState,
      setAdjustPriceOpen: false,
      unitType: null,
      updatedPrice: "",
    }));

    void loadAccommodationRatePlanInformation();
  }

  const handleSaveTaxLines = async () => {
    const params = {
      id: Number(ratePlanId),
      tax_line_ids: selectedTaxLine,
    };

    const putRes = await PutAccommodationRatePlanTaxLines(params, true);

    if (putRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error updating tax lines")));
      return;
    }

    dispatch(showSuccess(t("Successfully updated tax lines")));
    setSelectedTaxLineBeforeChanges([...selectedTaxLine]);

    setAccommodationRatePlanUpdateState(prevState => ({
      ...prevState,
      setTaxLinesOpen: false,
    }));
  };

  return (
    <div className="edit-rate-plan-wrapper">
      <Page
        title={ratePlan?.title}
        subtitle="Manage the rates and availability for the selected unit types."
        breadcrumbs={[
          { label: t("Accommodations"), url: "/admin/accommodation/calendar" },
          { label: t("Rates & availability"), url: "/admin/accommodation/rates" },
          { label: t("Rate plans"), url: "/admin/accommodation/rates" },
          { label: ratePlan?.title, url: "/admin/accommodation/rates/details/" + ratePlan?.id },
        ]}
        notificationBarProps={{
          isVisible: !ignoreOrderCompare(selectedTaxLine, selectedTaxLineBeforeChanges),
          onAction: handleSaveTaxLines,
          onCancel: () => setSelectedTaxLine(selectedTaxLineBeforeChanges),
        }}
      >
        <Page.Section>
          <div style={{ display: "flex", overflowX: "auto", marginBottom: "20px" }}>
            <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange} />
          </div>
        </Page.Section>

        {selected === 0 ? (
          <Card
            title={t("Linked unit types")}
            subtitle="View and manage rate plans for unit type(s)."
            titleActions={[
              {
                action: null,
                content: (
                  <>
                    <NavigationDropdownNew
                      rightAlign
                      label={"Rate Plan Options"}
                      hideChevron
                      showPlainTextLabel
                      sections={[
                        [
                          {
                            label: t("Adjust base price"),
                            onClick: () =>
                              setAccommodationRatePlanUpdateState({
                                ...accommodationRatePlanUpdateState,
                                unitType: null,
                                setAdjustPriceOpen: true,
                              }),
                            icon: "sliders-h",
                          },
                          {
                            label: t("Edit length of stay"),
                            onClick: () =>
                              setAccommodationRatePlanUpdateState({
                                ...accommodationRatePlanUpdateState,
                                setLengthOfStayOpen: true,
                              }),
                            icon: "clock",
                          },
                        ],
                      ]}
                    />
                  </>
                ),
                buttonType: "secondary",
              },
              // {
              //   action: null,
              //   content: (
              //     <>
              //       <NavigationDropdownNew
              //         rightAlign
              //         label={<FontAwesomeIcon icon={faBarsFilter} />}
              //         hideChevron
              //         showPlainTextLabel
              //         sections={[
              //           [
              //             {
              //               label: t("Filter?"),
              //               onClick: () => console.log("Filter me"),
              //               icon: "filter",
              //             },
              //           ],
              //         ]}
              //       />
              //     </>
              //   ),
              //   buttonType: "secondary",
              // },
            ]}
          >
            <div style={{ marginTop: "24px" }}>
              <DataTable
                columns={[
                  { label: t("Unit type") },
                  { label: t("Price (per night)"), width: "25%" },
                  { label: t("Occupancy (min to max)") },
                  // { label: t("Active"), width: "10%" },
                  { label: "", width: "5%" },
                ]}
                noRoundedTop
                footer={{
                  tableLimit: tableState.limit,
                  tableOffset: tableState.offset,
                  handleTableOffset: direction => {
                    const newOffset =
                      direction === "next"
                        ? tableState.offset + tableState.limit
                        : Math.max(0, tableState.offset - tableState.limit);
                    setTableState(prev => ({ ...prev, offset: newOffset }));
                  },
                  disableNextOffset: unitTypes.length < tableState.limit, // Mock pagination logic
                }}
                overflow
              >
                {unitTypes.map(unit => {
                  return (
                    <tr key={unit.id}>
                      <td>{unit.title}</td>
                      <td>
                        <div className="rate-plan-table-price-input">
                          <span>
                            {unit.min_price !== undefined &&
                            unit.max_price !== undefined &&
                            unit.min_price !== unit.max_price
                              ? `$${unit.min_price.toFixed(2)} - $${unit.max_price}`
                              : unit.min_price !== undefined
                              ? `$${unit.min_price.toFixed(2)}`
                              : `$${Number(ratePlan?.base_price)}`}
                          </span>
                        </div>
                      </td>
                      <td>
                        {unit.base_occupancy} to {unit.max_occupancy} people
                      </td>
                      {/* <td>
                        <div className="toggle-switch-container">
                          <Toggle
                            size="small"
                          />
                        </div>
                      </td> */}
                      <td className="text-right">
                        <NavigationDropdownNew
                          rightAlign
                          label={<FontAwesomeIcon icon={faEllipsisVertical} />}
                          hideChevron
                          showPlainTextLabel
                          sections={[
                            [
                              {
                                label: t("Adjust price"),
                                onClick: () =>
                                  setAccommodationRatePlanUpdateState({
                                    ...accommodationRatePlanUpdateState,
                                    unitType: unit,
                                    setAdjustPriceOpen: true,
                                  }),
                                icon: "sliders-h",
                              },
                              {
                                label: t("Edit length of stay"),
                                onClick: () =>
                                  setAccommodationRatePlanUpdateState({
                                    ...accommodationRatePlanUpdateState,
                                    unitType: unit,
                                    setLengthOfStayOpen: true,
                                  }),
                                icon: "clock",
                              },
                              {
                                label: t("View unit type"),
                                onClick: () => history.push(`/admin/accommodation/unit-types/${unit.id}`),
                                icon: "bed",
                              },
                            ],
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </DataTable>
            </div>
          </Card>
        ) : null}

        {selected === 1 ? (
          <Card title={t("Applicable tax lines")} subtitle="Manage or edit tax line(s) for your rate plan.">
            <div style={{ paddingLeft: "24px", paddingRight: "24px" }}>
              <hr style={{ marginBottom: "0px" }} />
            </div>
            <Card.Section>
              <div>
                <SelectNew
                  id="tax-lines-select"
                  label={t("Tax Line(s)")}
                  placeholder={getTaxNames() ?? "Select tax lines"}
                  multiValue={selectedTaxLine}
                  options={detailsCard.taxLines.map(taxLine => ({
                    id: taxLine.id,
                    label: taxLine.name,
                  }))}
                  onChange={value => toggleTaxLines(value)}
                />
              </div>
            </Card.Section>
          </Card>
        ) : null}

        <Sheet
          open={accommodationRatePlanUpdateState?.setLengthOfStayOpen}
          title={t("Set length of stay")}
          supportText={
            "Add length of stay restrictions for " +
            (accommodationRatePlanUpdateState?.unitType?.title
              ? "your " + accommodationRatePlanUpdateState?.unitType?.title
              : "all unit types")
          }
          size="small"
          onCancel={() =>
            setAccommodationRatePlanUpdateState(prevState => ({
              ...prevState,
              setLengthOfStayOpen: false,
              updatedMinLos: "",
              updatedMaxLos: "",
              unitType: null,
            }))
          }
          cancelText={t("Cancel")}
          backDropCancel
          closable
          okText={t("Done")}
          onOk={updateLos}
          okDisabled={
            accommodationRatePlanUpdateState?.updatedMinLos === "" ||
            accommodationRatePlanUpdateState?.updatedMaxLos === ""
          }
        >
          <div>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  label={t("Min. days")}
                  id={"min_los"}
                  value={accommodationRatePlanUpdateState?.updatedMinLos}
                  onChange={e =>
                    setAccommodationRatePlanUpdateState(prevState => ({ ...prevState, updatedMinLos: e.target.value }))
                  }
                  placeholder="1"
                  autoFocus
                  type={"number"}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Input
                  label={t("Max. days (optional)")}
                  id={"max_los"}
                  value={accommodationRatePlanUpdateState?.updatedMaxLos}
                  onChange={e =>
                    setAccommodationRatePlanUpdateState(prevState => ({ ...prevState, updatedMaxLos: e.target.value }))
                  }
                  placeholder="0"
                  autoFocus
                  type={"number"}
                  helpText={'Enter "0" if you do not require a maximum length of stay.'}
                />
              </FormLayout.Group>
            </FormLayout>
          </div>
        </Sheet>

        <Sheet
          open={accommodationRatePlanUpdateState?.setAdjustPriceOpen}
          title={t("Update Prices")}
          supportText={
            accommodationRatePlanUpdateState?.unitType?.title
              ? "Update prices for " + accommodationRatePlanUpdateState?.unitType?.title
              : "Update base price for " + ratePlan?.title
          }
          size="small"
          onCancel={() =>
            setAccommodationRatePlanUpdateState(prevState => ({
              ...prevState,
              setAdjustPriceOpen: false,
              updatedPrice: "",
              unitType: null,
            }))
          }
          cancelText={t("Cancel")}
          backDropCancel
          closable
          okText={t("Update")}
          onOk={adjustRatePlanPrice}
          okDisabled={accommodationRatePlanUpdateState?.updatedPrice === ""}
        >
          <div>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  label={t("New price")}
                  id={"editAllPrice"}
                  value={accommodationRatePlanUpdateState?.updatedPrice}
                  onChange={e =>
                    setAccommodationRatePlanUpdateState(prevState => ({
                      ...prevState,
                      updatedPrice: e.target.value,
                    }))
                  }
                  placeholder="0"
                  autoFocus
                  type={"number"}
                />
              </FormLayout.Group>
            </FormLayout>
          </div>
        </Sheet>
      </Page>
    </div>
  );
}
